import React from "react";
import { Exercise, ExerciseContainer, EXERCISE_TYPES } from "../../../libs/exercises/types";
import { FolderBrowser } from "./FolderBrowser";

type Props = {
  exercises: Exercise[];
  containers: ExerciseContainer[];
  exerciseType: string;
  // Single selection (DEFAULT/STRIPPING/CHECK)
  selectedExerciseId: string | null;
  onSelectExercise: (id: string) => void;
  // Multi selection (SUPERSET)
  supersetExercises: { id: string; progressionId: string | null }[];
  onAddSupersetExercise: (id: string) => void;
  onRemoveSupersetExercise: (id: string) => void;
  // CHECK filter
  checkCategoryId?: number;
};

export const StepExercisePicker: React.FC<Props> = ({
  exercises,
  containers,
  exerciseType,
  selectedExerciseId,
  onSelectExercise,
  supersetExercises,
  onAddSupersetExercise,
  onRemoveSupersetExercise,
  checkCategoryId,
}) => {
  const isSuperset = exerciseType === EXERCISE_TYPES.SUPERSET;

  // Filter exercises for CHECK type
  const filteredExercises =
    exerciseType === "CHECK" && checkCategoryId
      ? exercises.filter(
          (e) => e.specialCategoryId != null && e.specialCategoryId === checkCategoryId
        )
      : exercises;

  const supersetIds = supersetExercises.map((se) => se.id);

  const handleToggleSuperset = (id: string) => {
    if (supersetIds.includes(id)) {
      onRemoveSupersetExercise(id);
    } else {
      if (supersetExercises.length < 5) {
        onAddSupersetExercise(id);
      }
    }
  };

  const renderExerciseCard = (
    exercise: Exercise,
    isSelected: boolean,
    onSelect: () => void
  ) => (
    <button
      onClick={onSelect}
      className={`flex flex-col gap-2 p-4 rounded-xl border-2 transition-all text-left w-full ${
        isSelected
          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-sm"
          : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p
          className={`text-sm font-medium truncate ${
            isSelected
              ? "text-blue-700 dark:text-blue-300"
              : "text-gray-900 dark:text-white"
          }`}
        >
          {exercise.name}
        </p>
        {isSelected && (
          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>
      {exercise.tags && exercise.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {exercise.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
            >
              {tag}
            </span>
          ))}
          {exercise.tags.length > 3 && (
            <span className="text-xs text-gray-400">
              +{exercise.tags.length - 3}
            </span>
          )}
        </div>
      )}
    </button>
  );

  const browser = (
    <FolderBrowser
      containers={containers}
      items={filteredExercises}
      getContainerId={(c) => c.id}
      getContainerName={(c) => c.name}
      getContainerColor={(c) => c.color}
      getContainerParentId={(c) => c.parentId ?? null}
      getItemId={(e) => e.id}
      getItemName={(e) => e.name}
      getItemContainerId={(e) =>
        e.idContainer != null ? e.idContainer : null
      }
      matchesSearch={(e, q) =>
        e.name.toLowerCase().includes(q) ||
        (e.notes ?? "").toLowerCase().includes(q) ||
        (e.tags || []).some((t) => t.toLowerCase().includes(q))
      }
      hierarchical={true}
      selectedItemId={isSuperset ? null : selectedExerciseId}
      onSelectItem={isSuperset ? undefined : onSelectExercise}
      multiSelect={isSuperset}
      selectedIds={isSuperset ? supersetIds : undefined}
      onToggleItem={isSuperset ? handleToggleSuperset : undefined}
      renderItemCard={renderExerciseCard}
      searchPlaceholder="Cerca esercizio (nome, tag, note)..."
      emptyMessage={
        exerciseType === "CHECK"
          ? "Nessun esercizio CHECK trovato. Crea un esercizio con una categoria speciale CHECK."
          : "Nessun esercizio. Creane dalla sezione Esercizi."
      }
    />
  );

  if (!isSuperset) {
    return <div className="h-full flex flex-col">{browser}</div>;
  }

  // SUPERSET layout: browser + basket
  return (
    <div className="h-full flex gap-4">
      {/* Browser */}
      <div className="flex-1 min-w-0 flex flex-col">{browser}</div>

      {/* Basket */}
      <div className="w-64 flex-shrink-0 flex flex-col border-l border-gray-200 dark:border-gray-600 pl-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Esercizi selezionati ({supersetExercises.length}/5)
        </h3>
        {supersetExercises.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Seleziona gli esercizi da combinare nel superset
          </p>
        ) : (
          <div className="flex flex-col gap-2 flex-1 overflow-y-auto">
            {supersetExercises.map((se, i) => {
              const ex = exercises.find((e) => e.id === se.id);
              return (
                <div
                  key={se.id}
                  className="flex items-center gap-2 p-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                >
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-medium">
                    {i + 1}
                  </span>
                  <span className="text-sm text-gray-800 dark:text-gray-200 truncate flex-1">
                    {ex?.name ?? se.id}
                  </span>
                  <button
                    onClick={() => onRemoveSupersetExercise(se.id)}
                    className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {supersetExercises.length >= 5 && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
            Massimo 5 esercizi nel superset
          </p>
        )}
      </div>
    </div>
  );
};
